import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { FormikHelpers } from 'formik';
import { useRequest } from 'ahooks';

import { useLayout, useTimer } from '~hooks';
import { Input, Subtitle, Form, Button, ArrowRight } from '~components';
import { ChecklistAccess } from '~services';
import { ONBOARDING_TOKEN } from '~constants/localStorageIndexes';

import { Values, OnboardingLocationState } from './types';
import validationSchema from './validation';
import * as Styled from './styled';

const fiveMinutesInSeconds = 5 * 60;

function TokenCode() {
  const [disabled, setDisabled] = useState(true);
  const { setLayout } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as OnboardingLocationState;
  const [cnpj, setCnpj] = useState<string>();
  const [cpf, setCpf] = useState<string>();

  const initialValues: Values = {
    token: '',
  };

  const { timer, resetTimer } = useTimer(fiveMinutesInSeconds);

  const { runAsync: fetchSendToken } = useRequest(ChecklistAccess.getToken, {
    manual: true,
  });

  const { runAsync: fetchSendSMS } = useRequest(ChecklistAccess.sms, {
    manual: true,
  });

  const handleSubmit = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    try {
      const code = values.token;
      const response = await fetchSendToken({ cnpj, cpf, code });

      window.localStorage.setItem(ONBOARDING_TOKEN, response.data.data.token);
      navigate(`/representantes/analise-de-documentos`);
    } catch (error) {
      helpers.setFieldError('token', 'Token inválido.');
    }
  };

  const resendSMS = async () => {
    await fetchSendSMS({ cnpj, cpf });

    resetTimer();
    setDisabled(true);
  };

  useEffect(() => {
    if (timer === 0) {
      setDisabled(false);
    }
  }, [timer]);

  useEffect(() => {
    setLayout({ header: { position: 'left' } });

    if (locationState?.cnpj || locationState?.cpf) {
      setCnpj(locationState.cnpj);
      setCpf(locationState.cpf);
      return;
    }

    navigate('/');
  }, []);

  return (
    <Form<Values>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      aria-labelledby="form-token-code"
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {({ isValid }) => {
        return (
          <>
            <Styled.Title>Código Token</Styled.Title>
            <Subtitle>Digite o código Token que foi enviado via SMS.</Subtitle>
            <Input
              name="token"
              label="Código Token *"
              maxLength={6}
              width="300px"
              placeholder="Digite seu token"
              trailingIcon={
                <Button
                  disabled={!isValid}
                  type="submit"
                  variant="icon"
                  aria-label="continue"
                >
                  <ArrowRight />
                </Button>
              }
            />
            <Styled.CountdownDescription>
              <Styled.Countdown variant="sub200" tag="span">
                {moment(timer * 1000).format('mm:ss')}
              </Styled.Countdown>{' '}
              para solicitar um novo SMS.
            </Styled.CountdownDescription>
            <Styled.Button
              aria-label="resend-sms"
              onClick={resendSMS}
              icon={<Styled.PhoneMessage />}
              disabled={disabled}
            >
              Reenviar SMS
            </Styled.Button>
          </>
        );
      }}
    </Form>
  );
}

export default TokenCode;
